import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import type * as am5 from '@amcharts/amcharts5';
import { TranslateService } from '@ngx-translate/core';
import { AiAnalystDailyCredits } from 'src/app/models/aiAnalystSuccessMetricsSummary.model';
import { ChartService } from 'src/app/services/chart.service';
import { buildMonthDays } from '../ai-analyst-success-metrics/ai-analyst-success-metrics.util';

const BAR_COLOR = '#4B49B8';
const AXIS_TEXT_COLOR = '#6B7280';
const GRID_COLOR = '#EEF0F3';
const AXIS_FONT_SIZE = 10.5;
const BAR_WIDTH_PERCENT = 70;
const BAR_CORNER_RADIUS = 3;
const LABEL_MIN_GRID_DISTANCE = 24;
const DEFAULT_LOCALE = 'en';

@Component({
  standalone: false,
  selector: 'app-credits-per-day-chart',
  templateUrl: './credits-per-day-chart.component.html',
  styleUrls: ['./credits-per-day-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditsPerDayChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() month = '';
  @Input() dailyCredits: AiAnalystDailyCredits[] = [];

  @ViewChild('chartDiv') chartDiv?: ElementRef<HTMLElement>;

  private root?: am5.Root;
  private viewReady = false;
  private destroyed = false;
  private buildToken = 0;

  constructor(
    private readonly chartService: ChartService,
    private readonly translateService: TranslateService,
    private readonly zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewReady && (changes['month'] || changes['dailyCredits'])) {
      void this.buildChart();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.buildChart();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disposeChart();
  }

  private async buildChart(): Promise<void> {
    const token = ++this.buildToken;
    const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
    if (this.destroyed || token !== this.buildToken || !this.chartDiv || !this.month) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.disposeChart();

      const locale = this.translateService.currentLang || DEFAULT_LOCALE;
      const rows = buildMonthDays(this.month, this.dailyCredits).map(entry => ({
        day: String(entry.day),
        dayLabel: entry.date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
        credits: entry.credits ?? undefined
      }));
      const axisText = am5Ns.color(AXIS_TEXT_COLOR);

      const root = this.chartService.createRoot(this.chartDiv!.nativeElement);
      const xyChart = root.container.children.push(am5xyNs.XYChart.new(root, { panX: false, panY: false }));

      const xRenderer = am5xyNs.AxisRendererX.new(root, { minGridDistance: LABEL_MIN_GRID_DISTANCE });
      xRenderer.grid.template.set('visible', false);
      xRenderer.labels.template.setAll({ fill: axisText, fontSize: AXIS_FONT_SIZE });
      const xAxis = xyChart.xAxes.push(am5xyNs.CategoryAxis.new(root, { categoryField: 'day', renderer: xRenderer }));
      xAxis.data.setAll(rows);

      const yRenderer = am5xyNs.AxisRendererY.new(root, {});
      yRenderer.grid.template.setAll({ stroke: am5Ns.color(GRID_COLOR), strokeOpacity: 1 });
      yRenderer.labels.template.setAll({ fill: axisText, fontSize: AXIS_FONT_SIZE });
      const yAxis = xyChart.yAxes.push(am5xyNs.ValueAxis.new(root, { renderer: yRenderer, min: 0 }));

      const series = xyChart.series.push(am5xyNs.ColumnSeries.new(root, {
        xAxis,
        yAxis,
        categoryXField: 'day',
        valueYField: 'credits',
        fill: am5Ns.color(BAR_COLOR),
        tooltip: am5Ns.Tooltip.new(root, {
          labelText: `{dayLabel}: [bold]{valueY.formatNumber('#,##0.0')}[/] ${this.translateService.instant('credits')}`
        })
      }));
      series.columns.template.setAll({
        width: am5Ns.percent(BAR_WIDTH_PERCENT),
        cornerRadiusTL: BAR_CORNER_RADIUS,
        cornerRadiusTR: BAR_CORNER_RADIUS,
        strokeOpacity: 0
      });
      series.data.setAll(rows);

      this.root = root;
    });
  }

  private disposeChart(): void {
    this.root?.dispose();
    this.root = undefined;
  }
}
